/**
 * Harper underlines for the ProseMirror editor (#250), over the shared
 * engine (`harperEngine.ts`). Each textblock of the plain-text projection is
 * linted (and cached) separately; findings map back to document ranges and
 * skip entity names. Fixes and "Add to dictionary" open as a `hoverCards`
 * card (see `lintHoverSource`).
 */

import { Plugin, PluginKey, type EditorState } from "prosemirror-state";
import { Decoration, DecorationSet, type EditorView } from "prosemirror-view";
import { isJust, isNothing } from "../../lib/maybe";
import { createTimeoutHandle } from "../../lib/timeoutHandle";
import {
  blockText,
  textOffsetToDocPos,
  type PlainTextProjection,
} from "../../lib/zwdoc/plainText";
import {
  addHarperDictionaryWord,
  harperFixEdit,
  lintBlocks,
  recoverFromLintFailure,
  registerHarperClient,
  type HarperFinding,
  type HarperFix,
} from "../harperEngine";
import { entityMatchesOf, type ProseEntityMatch } from "./entityPeek";
import type { HoverSource } from "./hoverCards";
import { projectionOf } from "./projection";

/** Typing pause before a lint pass. */
const LINT_DELAY_MS = 650;
/** Hover delay before the fix menu opens. */
const LINT_HOVER_MS = 300;

type LintSpec = { finding: HarperFinding };

type LintState = {
  decorations: DecorationSet;
  /** Bumped by `relintHarper` so the view runs a fresh pass. */
  epoch: number;
};

type LintMeta =
  { kind: "set"; decorations: DecorationSet } | { kind: "relint" };

const lintKey = new PluginKey<LintState>("zwHarper");

function overlapsEntity(
  textFrom: number,
  textTo: number,
  matches: readonly ProseEntityMatch[],
): boolean {
  return matches.some((m) => textFrom < m.textTo && m.textFrom < textTo);
}

/** Document decorations for per-block findings. */
export function lintDecorations(
  state: EditorState,
  projection: PlainTextProjection,
  perBlock: readonly (readonly HarperFinding[])[],
): DecorationSet {
  const entities = entityMatchesOf(state);
  const decorations: Decoration[] = [];
  projection.blocks.forEach((block, index) => {
    for (const finding of perBlock[index] ?? []) {
      const textFrom = block.textFrom + finding.from;
      const textTo = block.textFrom + finding.to;
      if (textFrom >= textTo) continue;
      if (overlapsEntity(textFrom, textTo, entities)) continue;
      const from = textOffsetToDocPos(projection, textFrom);
      const to = textOffsetToDocPos(projection, textTo);
      if (from >= to) continue;
      const spec: LintSpec = { finding };
      decorations.push(
        Decoration.inline(
          from,
          to,
          { class: `zw-lint zw-lint-${finding.severity}` },
          spec,
        ),
      );
    }
  });
  return DecorationSet.create(state.doc, decorations);
}

/**
 * Debounced Harper underlines while `enabled()`. Must come after
 * `projectionPlugin` and `entityPeekPlugin`.
 */
export function harperPlugin(enabled: () => boolean): Plugin<LintState> {
  return new Plugin<LintState>({
    key: lintKey,
    state: {
      init: () => ({ decorations: DecorationSet.empty, epoch: 0 }),
      apply(tr, value) {
        const meta = tr.getMeta(lintKey) as LintMeta | undefined;
        if (meta?.kind === "set")
          return { ...value, decorations: meta.decorations };
        if (meta?.kind === "relint") {
          return {
            decorations: enabled() ? value.decorations : DecorationSet.empty,
            epoch: value.epoch + 1,
          };
        }
        return tr.docChanged
          ? { ...value, decorations: value.decorations.map(tr.mapping, tr.doc) }
          : value;
      },
    },
    props: {
      decorations: (state) => lintKey.getState(state)?.decorations ?? null,
    },
    view(view) {
      const timer = createTimeoutHandle();
      let pass = 0;
      let destroyed = false;

      const run = async (): Promise<void> => {
        if (destroyed || enabled() === false) return;
        const mine = ++pass;
        const { doc } = view.state;
        const projection = projectionOf(view.state);
        const texts = projection.blocks.map((_b, i) =>
          blockText(projection, i),
        );
        const isCurrent = (): boolean =>
          destroyed === false &&
          mine === pass &&
          view.state.doc === doc &&
          enabled();
        try {
          const perBlock = await lintBlocks(texts, isCurrent);
          if (isNothing(perBlock) || isCurrent() === false) return;
          const decorations = lintDecorations(
            view.state,
            projection,
            perBlock.value,
          );
          showLints(view, decorations);
        } catch (error) {
          if (recoverFromLintFailure(error) === "retry" && destroyed === false)
            schedule(0);
        }
      };

      const schedule = (delay = LINT_DELAY_MS): void => {
        if (enabled() === false) {
          timer.clear();
          return;
        }
        timer.schedule(delay, () => void run());
      };

      const unregister = registerHarperClient(async () => {
        await run();
      });
      schedule();

      return {
        update(current, prev) {
          const epochChanged =
            lintKey.getState(current.state)?.epoch !==
            lintKey.getState(prev)?.epoch;
          if (epochChanged) schedule(0);
          else if (current.state.doc !== prev.doc) schedule();
        },
        destroy() {
          destroyed = true;
          timer.clear();
          unregister();
        },
      };
    },
  });
}

/** Replace the underlines (a finished lint pass). */
export function showLints(view: EditorView, decorations: DecorationSet): void {
  view.dispatch(
    view.state.tr
      .setMeta(lintKey, { kind: "set", decorations } satisfies LintMeta)
      .setMeta("addToHistory", false),
  );
}

/** Re-lint now (enabled flag or entity index changed). */
export function relintHarper(view: EditorView): void {
  view.dispatch(
    view.state.tr
      .setMeta(lintKey, { kind: "relint" } satisfies LintMeta)
      .setMeta("addToHistory", false),
  );
}

export type ProseLint = { from: number; to: number; finding: HarperFinding };

/** The underline covering `pos` (or touching it on `side`). */
export function lintAt(
  state: EditorState,
  pos: number,
  side: -1 | 1,
): ProseLint | null {
  const decorations = lintKey.getState(state)?.decorations;
  if (decorations === undefined) return null;
  const found = decorations
    .find(pos - 1, pos + 1)
    .find((d) =>
      side > 0 ? d.from <= pos && pos < d.to : d.from < pos && pos <= d.to,
    );
  if (found === undefined) return null;
  const spec = found.spec as LintSpec;
  return { from: found.from, to: found.to, finding: spec.finding };
}

/** Apply a fix with `insertText`, so the replacement keeps the text's marks. */
export function applyHarperFix(
  view: EditorView,
  lint: { from: number; to: number },
  fix: HarperFix,
): void {
  const edit = harperFixEdit(fix, lint.from, lint.to);
  const tr =
    edit.insert === ""
      ? view.state.tr.delete(edit.from, edit.to)
      : view.state.tr.insertText(edit.insert, edit.from, edit.to);
  view.dispatch(tr.scrollIntoView());
  view.focus();
}

function lintMenu(view: EditorView, lint: ProseLint): HTMLElement {
  const list = document.createElement("ul");
  list.className = "zw-prose-lint-list";
  const item = document.createElement("li");
  item.className = `zw-lint-item zw-lint-item-${lint.finding.severity}`;
  const message = document.createElement("span");
  message.className = "zw-lint-message";
  message.textContent = lint.finding.message;
  item.appendChild(message);

  const action = (label: string, apply: () => void): void => {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "zw-lint-action";
    button.textContent = label;
    // Keep focus (and the selection) in the editor.
    button.addEventListener("mousedown", (event) => event.preventDefault());
    button.addEventListener("click", (event) => {
      event.preventDefault();
      apply();
    });
    item.appendChild(button);
  };

  for (const fix of lint.finding.fixes) {
    action(fix.label, () => applyHarperFix(view, lint, fix));
  }
  const word = lint.finding.dictionaryWord;
  if (isJust(word)) {
    action("Add to dictionary", () => {
      void addHarperDictionaryWord(word.value);
    });
  }
  list.appendChild(item);
  return list;
}

/** Hover card listing a lint's fixes. */
export function lintHoverSource(): HoverSource {
  return {
    hoverDelayMs: LINT_HOVER_MS,
    at(view, pos, side) {
      const lint = lintAt(view.state, pos, side);
      if (lint === null) return null;
      return {
        from: lint.from,
        to: lint.to,
        above: false,
        className: "zw-lint-menu zw-prose-popover-lint",
        render: () => lintMenu(view, lint),
      };
    },
  };
}
